import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormCategory, FormTemplateType } from '@nrwl/rx-models';
import { FeatureFlagsStore, FeatureKey } from '@nrwl/rx-state-feature-flags';
import { SnackbarService } from '@nrwl/snackbar-services';
import { Store } from '@ngrx/store';
import * as fromManager from '@nrwl/rx-root-state';
import * as fromActions from 'libs/rx-state/actions/index';
import { RxButtonModule } from '@nrwl/rx-button';
import { RxIconModule } from '@nrwl/rx-icon';
import { InfiniteScrollTriggerDirective } from '@nrwl/rx-directives';
import { RxFormOptionCardComponent } from './components/rx-form-option-card/rx-form-option-card.component';

type CreateFormDialogStep = 'options' | 'upload' | 'category';

interface FormOption {
  type: FormTemplateType;
  icon: string;
  title: string;
  description: string;
}

interface CreateFormDialogPayload {
  title: string;
  isRxStaff: boolean;
  fields: Array<{ value: unknown }>;
  buttons: Record<string, never>;
}

@Component({
  selector: 'lib-rx-create-form-dialog',
  templateUrl: './rx-create-form-dialog.component.html',
  styleUrls: ['./rx-create-form-dialog.component.scss'],
  imports: [
    CommonModule,
    RxButtonModule,
    RxIconModule,
    InfiniteScrollTriggerDirective,
    RxFormOptionCardComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RxCreateFormDialogComponent implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogRef = inject(MatDialogRef);
  private readonly featureFlagsStore = inject(FeatureFlagsStore);
  private readonly snackbarService = inject(SnackbarService);
  private readonly store = inject(Store);

  readonly payload = input.required<CreateFormDialogPayload>();

  public readonly options = computed<FormOption[]>(() => {
    const options: FormOption[] = [
      {
        type: FormTemplateType.Fillable,
        icon: 'icon-form-entry-fillable',
        title: 'Fillable Form',
        description: 'Used once and completed by the patient',
      },
    ];

    if (this.featureFlagsStore.flagsSignal().has(FeatureKey.PERSONALIZED_PLANS)) {
      options.push({
        type: FormTemplateType.PersonalizedPlan,
        icon: 'icon-form-entry-plan',
        title: 'Personalized Plan',
        description: 'Updated over time to track ongoing patient care',
      });
    }

    options.push({
      type: FormTemplateType.Pdf,
      icon: 'icon-form-entry-pdf',
      title: 'PDF Form',
      description: 'Upload a read-only PDF form for patient use',
    });

    return options;
  });

  readonly step = signal<CreateFormDialogStep>('options');
  readonly selectedFile = signal<File | null>(null);
  readonly selectedCategory = signal<FormCategory | null>(null);
  readonly categories = signal<FormCategory[]>([]);
  readonly categoriesLoading = signal(false);
  readonly categoriesLoadingMore = signal(false);
  readonly categoriesHasMore = signal(false);
  readonly categoriesLoaded = signal(false);

  public ngOnInit(): void {
    const isGlobal = this.payload().isRxStaff;

    this.store
      .select(
        isGlobal ? fromManager.getLoadedGlobalCategories : fromManager.getLoadedAccountCategories,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((categories: FormCategory[] | null) => this.categories.set(categories ?? []));

    this.store
      .select(isGlobal ? fromManager.getGlobalCategoriesBusy : fromManager.getAccountCategoriesBusy)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loading) => this.categoriesLoading.set(loading));

    this.store
      .select(
        isGlobal
          ? fromManager.getGlobalCategoriesLoadingMore
          : fromManager.getAccountCategoriesLoadingMore,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loading) => this.categoriesLoadingMore.set(loading));

    this.store
      .select(
        isGlobal ? fromManager.getGlobalCategoriesHasMore : fromManager.getAccountCategoriesHasMore,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((hasMore) => this.categoriesHasMore.set(hasMore));

    this.store
      .select(
        isGlobal ? fromManager.getGlobalCategoriesLoaded : fromManager.getAccountCategoriesLoaded,
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((loaded) => this.categoriesLoaded.set(loaded));
  }

  public selectOption(option: FormOption): void {
    this.payload().fields[0].value = option.type;

    if (option.type === FormTemplateType.Pdf) {
      this.loadCategories();
      this.step.set('upload');
      this.payload().title = 'Upload PDF Form';
      return;
    }

    this.submitSelection();
  }

  public close(): void {
    this.dialogRef.close(null);
  }

  public selectFile(event: Event): void {
    const files = (event.target as HTMLInputElement).files;
    this.selectedFile.set(files?.item(0) ?? null);
  }

  public clearFile(): void {
    this.selectedFile.set(null);
  }

  public continueToCategory(): void {
    if (!this.selectedFile()) {
      this.snackbarService.error('Please select a PDF file to upload.', 3000);
      return;
    }

    this.step.set('category');
    this.payload().title = 'Select Folder';
  }

  public selectCategory(category: FormCategory): void {
    this.selectedCategory.set(category);
  }

  public submitPdf(): void {
    if (!this.selectedCategory()) {
      this.snackbarService.error('Please select a Form Folder.', 3000);
      return;
    }

    const payload = this.payload();
    payload.fields[1].value = this.selectedCategory()?.id;
    payload.fields[2].value = this.selectedFile();
    payload.fields[3].value = this.selectedFile()?.name ?? '';
    this.dialogRef.close(payload);
  }

  public loadMoreCategories(): void {
    if (this.categoriesLoading() || this.categoriesLoadingMore() || !this.categoriesHasMore()) {
      return;
    }

    this.store.dispatch(
      this.payload().isRxStaff
        ? new fromActions.LoadMoreGlobalCategories()
        : new fromActions.LoadMoreAccountCategories(),
    );
  }

  private submitSelection(): void {
    const payload = this.payload();
    payload.fields[1].value = 0;
    payload.fields[2].value = null;
    payload.fields[3].value = '';
    this.dialogRef.close(payload);
  }

  private loadCategories(): void {
    if (this.categoriesLoading() || this.categoriesLoaded()) {
      return;
    }

    this.store.dispatch(
      this.payload().isRxStaff
        ? new fromActions.LoadGlobalCategories()
        : new fromActions.LoadAccountCategories(),
    );
  }
}
